import React, { useContext, useEffect, useMemo, useState } from 'react';
import AgentContext from '../../../context/admin/Agent.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import { Users } from '../../../models/User.tsx';
import { editUser, showUser } from '../../../services/admin/UserService.tsx';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import {
    ALL_PLAYER_LIMIT_FIELDS,
    LimitField,
    PLAYER_PAYOUT_LIMIT_FIELDS,
    PlayerLimitKey,
    WAGER_LIMIT_FIELDS,
    WagerLimitsForm,
    buildLimitsForm,
    emptyLimitsForm,
    PLAYER_LIMIT_NOT_SET,
    formatLimitChange,
    formatLimitDisplay,
    formatOddsInputLive,
    formatWagerInputLive,
    getStatusClass,
    parseLimitsForm,
    parseOddsInput,
    parseWagerInput,
} from './bettingSetupUtils.ts';
import UnsavedChangesModal from './UnsavedChangesModal.tsx';

type PlayerLimitsSetupProps = {
    onDirtyChange: (dirty: boolean) => void;
    onToast: (type: 'success' | 'error', message: string) => void;
};

function PlayerLimitsSetup({ onDirtyChange, onToast }: PlayerLimitsSetupProps) {
    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();
    const { allPlayers, allAgents, updatePlayerInContext } = useContext(AgentContext);

    const [searchQuery, setSearchQuery] = useState('');
    const [agentFilter, setAgentFilter] = useState('all');
    const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
    const [limitsForm, setLimitsForm] = useState<WagerLimitsForm>(emptyLimitsForm());
    const [originalLimits, setOriginalLimits] = useState<WagerLimitsForm>(emptyLimitsForm());
    const [isSaving, setIsSaving] = useState(false);
    const [pendingPlayer, setPendingPlayer] = useState<Users | null>(null);

    if (!currentUser) {
        throw new Error('User not authenticated');
    }

    const isMasterAgent = currentUser.userType === 'masterAgent';

    const filteredPlayers = useMemo(() => {
        let players = [...allPlayers];

        if (agentFilter !== 'all') {
            players = players.filter(p => p.parent === agentFilter);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            players = players.filter(
                p =>
                    p.userName.toLowerCase().includes(q) ||
                    (p.name?.toLowerCase().includes(q) ?? false) ||
                    (p.parent?.toLowerCase().includes(q) ?? false)
            );
        }

        return players.sort((a, b) => a.userName.localeCompare(b.userName));
    }, [allPlayers, agentFilter, searchQuery]);

    const selectedPlayer = useMemo(
        () => filteredPlayers.find(p => p.id === selectedPlayerId) ?? null,
        [filteredPlayers, selectedPlayerId]
    );

    const hasUnsavedChanges = useMemo(
        () => ALL_PLAYER_LIMIT_FIELDS.some(({ key }) => limitsForm[key] !== originalLimits[key]),
        [limitsForm, originalLimits]
    );

    useEffect(() => {
        onDirtyChange(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    const applySelectPlayer = (player: Users) => {
        if (!player.id) return;
        setSelectedPlayerId(player.id);
        const form = buildLimitsForm(player);
        setLimitsForm(form);
        setOriginalLimits(form);
        setPendingPlayer(null);
    };

    const selectPlayer = (player: Users) => {
        if (!player.id) return;
        if (hasUnsavedChanges) {
            setPendingPlayer(player);
            return;
        }
        applySelectPlayer(player);
    };

    useEffect(() => {
        if (filteredPlayers.length === 0) {
            if (selectedPlayerId !== null && !hasUnsavedChanges) {
                setSelectedPlayerId(null);
                const empty = emptyLimitsForm();
                setLimitsForm(empty);
                setOriginalLimits(empty);
            }
            return;
        }

        const stillVisible = filteredPlayers.some(p => p.id === selectedPlayerId);
        if (!stillVisible && !hasUnsavedChanges) {
            applySelectPlayer(filteredPlayers[0]);
        }
        // Selecting the first visible player when the current one drops out of the filter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredPlayers, selectedPlayerId, hasUnsavedChanges]);

    const fieldByKey = (key: PlayerLimitKey) =>
        ALL_PLAYER_LIMIT_FIELDS.find(field => field.key === key)!;

    const handleLimitChange = (key: PlayerLimitKey, value: string) => {
        const kind = fieldByKey(key).kind;
        setLimitsForm(prev => ({
            ...prev,
            [key]: kind === 'odds' ? formatOddsInputLive(value) : formatWagerInputLive(value),
        }));
    };

    const handleLimitBlur = (key: PlayerLimitKey) => {
        const kind = fieldByKey(key).kind;
        const raw = limitsForm[key].trim().toLowerCase();
        if (raw === '' || raw === 'unlimited' || raw === 'not set' || raw === '0' || raw === '+0') {
            setLimitsForm(prev => ({ ...prev, [key]: PLAYER_LIMIT_NOT_SET }));
            return;
        }
        const parsed = kind === 'odds' ? parseOddsInput(limitsForm[key]) : parseWagerInput(limitsForm[key]);
        if (parsed > 0) {
            setLimitsForm(prev => ({ ...prev, [key]: formatLimitDisplay(key, kind, parsed) }));
        }
    };

    const handleReset = () => {
        setLimitsForm({ ...originalLimits });
    };

    const handleSavePlayer = async () => {
        if (!selectedPlayer?.id) return;

        setIsSaving(true);
        try {
            const freshUser = await showUser(selectedPlayer.userName);
            const newData = parseLimitsForm(limitsForm);

            const changes: string[] = [];
            ALL_PLAYER_LIMIT_FIELDS.forEach(({ key, kind }) => {
                const oldValue = freshUser[key] ?? 0;
                const newValue = newData[key];
                if (oldValue !== newValue) {
                    changes.push(
                        `${key}: ${formatLimitChange(key, kind, oldValue)} → ${formatLimitChange(key, kind, newValue)}`
                    );
                }
            });

            if (changes.length === 0) {
                onToast('success', 'No changes to save');
                return;
            }

            await editUser(selectedPlayer.id, newData);

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `Betting Setup: ${selectedPlayer.userName} | ${changes.join(', ')}`,
                    userIpDataAdmin
                );
            }

            updatePlayerInContext(selectedPlayer.id, newData);

            const updatedForm = buildLimitsForm({ ...selectedPlayer, ...newData });
            setLimitsForm(updatedForm);
            setOriginalLimits(updatedForm);
            onToast('success', `Limits updated for ${selectedPlayer.userName}`);
        } catch (error) {
            console.error(error);
            onToast('error', 'Error saving wager limits');
        } finally {
            setIsSaving(false);
        }
    };

    const renderLimitCards = (fields: LimitField[]) => (
        <div className="betting-setup-limits-grid">
            {fields.map(({ key, label, icon, description, kind }) => (
                <div key={key} className="betting-setup-limit-card">
                    <div className="betting-setup-limit-label">
                        <span className="betting-setup-limit-icon">{icon}</span>
                        {label}
                    </div>
                    <div className="betting-setup-limit-desc">{description}</div>
                    <div className="betting-setup-limit-input-wrap">
                        {kind === 'money' && <span className="betting-setup-limit-prefix">$</span>}
                        <input
                            type="text"
                            className="betting-setup-limit-input"
                            value={limitsForm[key]}
                            onChange={e => handleLimitChange(key, e.target.value)}
                            onBlur={() => handleLimitBlur(key)}
                            autoComplete="off"
                            disabled={isSaving}
                        />
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <div className="betting-setup-toolbar betting-setup-toolbar-inline">
                {isMasterAgent && allAgents.length > 0 && (
                    <select
                        className="betting-setup-agent-select"
                        value={agentFilter}
                        onChange={e => setAgentFilter(e.target.value)}
                    >
                        <option value="all">All Agents</option>
                        {allAgents.map((agent: Users) => (
                            <option key={agent.id ?? agent.userName} value={agent.userName}>
                                {agent.userName}
                            </option>
                        ))}
                    </select>
                )}
                <input
                    type="text"
                    className="betting-setup-search"
                    placeholder="Search players..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    autoComplete="off"
                />
            </div>

            <div className="betting-setup-layout">
                <div className="betting-setup-players-panel">
                    <div className="betting-setup-panel-header">
                        <span>Players</span>
                        <span className="betting-setup-player-count">{filteredPlayers.length}</span>
                    </div>
                    <div className="betting-setup-players-list">
                        {filteredPlayers.length === 0 ? (
                            <div className="betting-setup-empty">No players found</div>
                        ) : (
                            filteredPlayers.map(player => (
                                <div
                                    key={player.id}
                                    className={`betting-setup-player-item ${selectedPlayerId === player.id ? 'active' : ''}`}
                                    onClick={() => selectPlayer(player)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => e.key === 'Enter' && selectPlayer(player)}
                                >
                                    <div className="betting-setup-player-info">
                                        <span className="betting-setup-player-name">{player.userName}</span>
                                        {player.name && (
                                            <span className="betting-setup-player-agent">{player.name}</span>
                                        )}
                                        {isMasterAgent && player.parent && (
                                            <span className="betting-setup-player-agent">Agent: {player.parent}</span>
                                        )}
                                    </div>
                                    <span className={`betting-setup-player-status ${getStatusClass(player.status)}`}>
                                        {player.status}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="betting-setup-editor-panel">
                    <div className="betting-setup-panel-header">
                        <span>Wager Limits</span>
                    </div>

                    {!selectedPlayer ? (
                        <div className="betting-setup-empty">Select a player to edit wager limits</div>
                    ) : (
                        <div className="betting-setup-editor-content">
                            <div className="betting-setup-selected-user">
                                <div>
                                    <div className="betting-setup-selected-name">{selectedPlayer.userName}</div>
                                    <div className="betting-setup-selected-meta">
                                        {selectedPlayer.name && `${selectedPlayer.name} · `}
                                        Agent: {selectedPlayer.parent ?? 'N/A'}
                                    </div>
                                </div>
                                <span className={`betting-setup-player-status ${getStatusClass(selectedPlayer.status)}`}>
                                    {selectedPlayer.status}
                                </span>
                            </div>

                            <p className="betting-setup-hint">
                                Enter 0 or &quot;Not set&quot; when this player has no own limit. A number overrides the agent.
                            </p>

                            {renderLimitCards(WAGER_LIMIT_FIELDS)}
                            <div className="betting-setup-section-label">Payout &amp; odds</div>
                            {renderLimitCards(PLAYER_PAYOUT_LIMIT_FIELDS)}

                            <div className="betting-setup-actions">
                                {hasUnsavedChanges && (
                                    <span className="betting-setup-unsaved">Unsaved changes</span>
                                )}
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-reset"
                                    onClick={handleReset}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    Reset
                                </button>
                                <button
                                    type="button"
                                    className="betting-setup-btn betting-setup-btn-save"
                                    onClick={handleSavePlayer}
                                    disabled={!hasUnsavedChanges || isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <UnsavedChangesModal
                show={pendingPlayer !== null}
                onKeepEditing={() => setPendingPlayer(null)}
                onDiscard={() => {
                    if (pendingPlayer) applySelectPlayer(pendingPlayer);
                }}
            />
        </>
    );
}

export default PlayerLimitsSetup;
